import { useCallback, useEffect } from "react";
import { useHistory } from "react-router";
import { TransitionPromptHook } from "history";
import { isMobile } from "src/utils/userSegment";
import { isDevMode } from "src/utils/userSegment/features";
import { useCallbackRef } from "./lifecycle";
import { isGoing } from "../pageDefinitions/components/PageSetRouter";
import goto from "src/pageDefinitions/goto";
import { GoToActions } from "@pageDefinitions/types";
import { captureException } from "src/utils/error";

export function useBeforeUnloadAlert(
  enabledParam = true,
  cb: (e: BeforeUnloadEvent) => boolean = () => true
) {
  const enabled = !isMobile() && !isDevMode() && enabledParam;

  const callbackRef = useCallbackRef(cb);
  const handleEvent = useCallback(
    (e: BeforeUnloadEvent) => {
      // Disable if we are in a code initiated navigation.
      if (isGoing()) {
        return;
      }

      const ret = callbackRef(e);

      if (ret) {
        e.preventDefault();
        // Fix for Chrome and Edge
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
        e.returnValue = "";
      }
    },
    [callbackRef]
  );
  const cleanup = useCallback(() => {
    window.removeEventListener("beforeunload", handleEvent);
  }, [handleEvent]);

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    window.addEventListener("beforeunload", handleEvent);
    return cleanup;
  }, [handleEvent, cleanup, enabled]);

  return cleanup;
}

/**
 * Exposes history.block in a hook, allowing for navigation to be blocked.
 */
export function useNavigationBlock<T>(cb: TransitionPromptHook<T>) {
  const history = useHistory<T>();
  const callbackRef = useCallbackRef(cb);

  useEffect(() => {
    return history.block((location, action) => {
      // HACK around our pushState hack: Check for path change manually
      // This can be removed after the push state hack is removed
      let realAction = action;
      if (action === "POP" && isGoing()) {
        realAction = "PUSH";
      }

      return callbackRef(location, realAction);
    });
  }, [history, callbackRef]);
}

export function executeGotoAction(gotoAction: GoToActions) {
  const actions = Object.entries(gotoAction || {}).filter(
    ([key]) => key !== "//"
  );

  if (!actions.length) {
    captureException(
      new Error(`${executeGotoAction.name} called with no actions`)
    );

    return;
  }

  const [actionName, args = []] = actions[0];
  if (!goto[actionName] || typeof goto[actionName] !== "function") {
    captureException(
      new Error(
        `${executeGotoAction.name} called with invalid action: ${actionName}`
      )
    );

    return;
  }

  goto[actionName](...args);
}
